import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Handshake, Pencil, Hammer, Truck,
    ShieldCheck, Wrench, Sparkles, Layers, Settings2, FileText,
    Eye, ClipboardCheck, BookOpen, MessageCircle, Award, Lightbulb,
    Wand2, Replace, X, Check, Clock, ChevronRight, Zap, MapPin, Camera, Users, Palette,
} from "lucide-react";
import { usePageContent, useContentList, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";
import { resolveIcon } from "@/lib/cmsHelpers";
import CtaBanner from "@/components/CtaBanner";
import InlineCTA from "@/components/InlineCTA";

/* Icon defaults per phase step position */
const STEP_ICON_DEFAULTS = {
    "01": [MessageCircle, Lightbulb, Eye, FileText],
    "02": [ClipboardCheck, Layers, Settings2, Wand2, Award, Replace],
    "03": [BookOpen, Handshake, Wrench, ShieldCheck],
};

const PHASES_FALLBACK = [
    {
        number: "01", title: "Design", subtitle: "Design colaborativo", icon: "Pencil",
        body: "Trabalhamos com cada cliente para criar um carrinho que reflecte a sua marca, fluxo de trabalho e ambição. Da primeira ideia ao desenho final em 2 a 4 semanas.",
        steps: "Consulta inicial — discussão de objectivos e menu|Reuniões de design e layout|Renders 3D fotorrealistas|Orçamento detalhado e transparente",
    },
    {
        number: "02", title: "Produção", subtitle: "Engenharia e construção", icon: "Hammer",
        body: "Cada NOVIFOOD é construído à mão na nossa oficina em Portugal, num processo de 6 fases que combina engenharia precisa e acabamento artesanal.",
        steps: "Pré-produção e validação técnica|Estrutura em alumínio e marcenaria|Eléctrica e canalizações certificadas|Acabamentos e branding|Testes finais — 60+ pontos|Inspecção pelo cliente",
    },
    {
        number: "03", title: "Entrega & Suporte", subtitle: "Onboarding e operação", icon: "Truck",
        body: "A relação não termina na entrega. Acompanhamos cada cliente durante a vida útil do equipamento — manuais, formação e suporte técnico imediato.",
        steps: "Materiais de apoio e manuais digitais|Onboarding 1-a-1 e formação|Assistência técnica em 24h|Garantia alargada de 2 anos",
    },
];

const PHILOSOPHY_FALLBACK = [
    { icon: "Eye", title: "Transparência radical", desc: "Mostramos cada custo, cada decisão de design e cada passo de produção. Sem letras pequenas." },
    { icon: "Users", title: "Cliente no centro", desc: "Adaptamos cada projecto às necessidades reais do seu negócio. Não há dois carrinhos iguais." },
    { icon: "Leaf", title: "Sustentabilidade", desc: "Materiais recicláveis, energia solar e fornecedores locais sempre que possível." },
    { icon: "Award", title: "Qualidade premium", desc: "Componentes certificados, soldas industriais, acabamentos pensados para durar 10+ anos." },
    { icon: "Clock", title: "Prazos cumpridos", desc: "Calendário transparente desde o briefing. Atrasos? Comunicamos antes que aconteçam." },
    { icon: "Heart", title: "Suporte vitalício", desc: "Garantia de 2 anos, suporte técnico em 24h, peças de reposição sempre disponíveis." },
];

const PHASES_EN = [
    {
        number: "01", title: "Design", subtitle: "Collaborative design", icon: "Pencil",
        body: "We work with each client to create a cart that reflects their brand, workflow and ambition. From first idea to final drawing in 2 to 4 weeks.",
        steps: "Initial consultation — goals and menu discussion|Design and layout meetings|Photorealistic 3D renders|Detailed, transparent quote",
    },
    {
        number: "02", title: "Production", subtitle: "Engineering and build", icon: "Hammer",
        body: "Every NOVIFOOD is hand-built in our workshop in Portugal, in a 6-stage process combining precise engineering and artisan finishing.",
        steps: "Pre-production and technical validation|Aluminium structure and carpentry|Certified electrics and plumbing|Finishing and branding|Final tests — 60+ checkpoints|Client inspection",
    },
    {
        number: "03", title: "Delivery & Support", subtitle: "Onboarding and operation", icon: "Truck",
        body: "The relationship doesn't end at delivery. We support every client throughout the equipment's life — manuals, training and immediate technical support.",
        steps: "Support materials and digital manuals|1-on-1 onboarding and training|Technical assistance within 24h|Extended 2-year warranty",
    },
];

const PHILOSOPHY_EN = [
    { icon: "Eye", title: "Radical transparency", desc: "We show every cost, every design decision and every production step. No small print." },
    { icon: "Users", title: "Client at the centre", desc: "We adapt every project to your business's real needs. No two carts are alike." },
    { icon: "Leaf", title: "Sustainability", desc: "Recyclable materials, solar power and local suppliers whenever possible." },
    { icon: "Award", title: "Premium quality", desc: "Certified components, industrial welds, finishes built to last 10+ years." },
    { icon: "Clock", title: "Deadlines met", desc: "Transparent schedule from the briefing. Delays? We communicate before they happen." },
    { icon: "Heart", title: "Lifetime support", desc: "2-year warranty, 24h technical support, spare parts always available." },
];

/* Extra metadata not in CMS (duration / deliverables / stat) — keyed by phase number. */
const PHASE_META = {
    "01": {
        pt: {
            duration: "2 a 4 semanas",
            deliverables: ["Briefing detalhado", "Renders 3D", "Orçamento fechado"],
            stat: { value: "48h", label: "para o primeiro render" },
            cta: { title: "Vê renders reais de carrinhos que fizemos.", to: "/galeria", label: "Ir para a galeria", icon: Camera },
        },
        en: {
            duration: "2 to 4 weeks",
            deliverables: ["Detailed briefing", "3D renders", "Locked-in quote"],
            stat: { value: "48h", label: "to first render" },
            cta: { title: "See real renders of carts we've built.", to: "/galeria", label: "Go to gallery", icon: Camera },
        },
    },
    "02": {
        pt: {
            duration: "4 a 6 semanas",
            deliverables: ["Carrinho testado", "60+ inspecções", "Fotos da produção"],
            stat: { value: "60+", label: "pontos de inspecção" },
            cta: { title: "Conhece a oficina e a equipa portuguesa.", to: "/sobre", label: "Sobre a Novifood", icon: Users },
        },
        en: {
            duration: "4 to 6 weeks",
            deliverables: ["Tested cart", "60+ inspections", "Production photos"],
            stat: { value: "60+", label: "inspection points" },
            cta: { title: "Meet the workshop and the Portuguese team.", to: "/sobre", label: "About Novifood", icon: Users },
        },
    },
    "03": {
        pt: {
            duration: "Contínuo",
            deliverables: ["Manuais digitais", "Formação 1-a-1", "Garantia 2 anos"],
            stat: { value: "24h", label: "SLA de resposta técnica" },
            cta: { title: "Vê o que dizem quem já opera com NOVIFOOD.", to: "/sobre#testemunhos", label: "Ler testemunhos", icon: MessageCircle },
        },
        en: {
            duration: "Ongoing",
            deliverables: ["Digital manuals", "1-on-1 training", "2-year warranty"],
            stat: { value: "24h", label: "technical response SLA" },
            cta: { title: "See what active NOVIFOOD operators say.", to: "/sobre#testemunhos", label: "Read testimonials", icon: MessageCircle },
        },
    },
};

/* Rough duration to render on each step chip (per phase index → step index). Keeps UI honest without hard promises. */
const STEP_DURATIONS = {
    "01": ["30 min", "3-5 dias", "5-7 dias", "1-2 dias"],
    "02": ["3-5 dias", "2 sem", "1 sem", "3-5 dias", "3-5 dias", "1 dia"],
    "03": ["Dia 1", "Semana 1", "24h SLA", "2 anos"],
};

function parseSteps(stepsStr, number) {
    if (!stepsStr) return [];
    const defaults = STEP_ICON_DEFAULTS[number] || [];
    return stepsStr.split("|").map((s, i) => {
        const [labelPart, ...rest] = s.split("—");
        const label = (labelPart || "").trim();
        const text = rest.length ? rest.join("—").trim() : "";
        return { icon: defaults[i] || Sparkles, label, text };
    });
}

/* Sticky phase progress dots — highlights the current in-view phase using IntersectionObserver */
function usePhaseProgress(phaseNumbers) {
    const [active, setActive] = useState(phaseNumbers[0] || null);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const observers = [];
        phaseNumbers.forEach((n) => {
            const el = document.getElementById(`fase-${n}`);
            if (!el) return;
            const obs = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) setActive(n);
                    });
                },
                { rootMargin: "-40% 0px -50% 0px", threshold: 0 }
            );
            obs.observe(el);
            observers.push(obs);
        });
        return () => observers.forEach((o) => o.disconnect());
    }, [phaseNumbers.join(",")]);
    return active;
}

export default function Processos() {
    const { t } = usePageContent("processos");
    const { tr, en } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");
    const cmsPhases = useContentList("processos_phases", PHASES_FALLBACK, { forceFallbackEn: false });
    const cmsPhilosophy = useContentList("processos_philosophy", PHILOSOPHY_FALLBACK, { forceFallbackEn: false });
    const phasesData = en ? PHASES_EN : cmsPhases;
    const philosophy = en ? PHILOSOPHY_EN : cmsPhilosophy;

    useSeo({
        title: tr("Processos — Como construímos o seu food cart", "Process — How we build your food cart"),
        description: tr(
            "Do design ao branding, da produção à entrega: descubra o processo NOVIFOOD em 3 fases. Engenharia premium, materiais portugueses, entrega em 30 dias.",
            "From design to branding, production to delivery: discover the NOVIFOOD 3-phase process. Premium engineering, Portuguese materials, 30-day delivery."
        ),
        path: "/processos",
    });

    const phases = useMemo(() => phasesData.map((p) => {
        const meta = (PHASE_META[p.number] || {})[en ? "en" : "pt"] || {};
        return {
            n: p.number || "",
            title: p.title || "",
            kicker: p.subtitle || "",
            desc: p.body || "",
            icon: resolveIcon(p.icon, "Pencil"),
            steps: parseSteps(p.steps, p.number),
            durations: STEP_DURATIONS[p.number] || [],
            duration: meta.duration || "",
            deliverables: meta.deliverables || [],
            stat: meta.stat || null,
            cta: meta.cta || null,
        };
    }), [phasesData, en]);

    const phaseNumbers = phases.map((p) => p.n);
    const active = usePhaseProgress(phaseNumbers);

    /* Sem vs Com NOVIFOOD */
    const CONTRAST = en
        ? {
            outTitle: "Doing it alone",
            outItems: [
                "Weeks lost hunting welders, painters and electricians",
                "Improvised designs, no 3D validation before build",
                "No documented warranty or after-sales",
                "Unpredictable costs — €500 here, €800 there",
                "Delivery date? A guess.",
            ],
            inTitle: "With NOVIFOOD",
            inItems: [
                "One team, one contract, one delivery date",
                "Photorealistic 3D renders before a single bolt is turned",
                "2-year warranty and 24h SLA — in writing",
                "One closed quote, zero surprises",
                "Schedule locked at briefing — signed calendar",
            ],
        }
        : {
            outTitle: "Fazer sozinho",
            outItems: [
                "Semanas perdidas a procurar soldadores, pintores e eléctricos",
                "Designs improvisados, sem validação 3D antes de construir",
                "Sem garantia nem pós-venda documentado",
                "Custos imprevisíveis — €500 aqui, €800 ali",
                "Data de entrega? Um palpite.",
            ],
            inTitle: "Com NOVIFOOD",
            inItems: [
                "Uma equipa, um contrato, uma data de entrega",
                "Renders 3D fotorrealistas antes de mexer numa peça",
                "Garantia de 2 anos e SLA 24h — por escrito",
                "Um orçamento fechado, zero surpresas",
                "Calendário fechado no briefing — assinado",
            ],
        };

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="processos-hero">
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div aria-hidden className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }} />
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-[0.18em] uppercase text-white/85 backdrop-blur">
                        <Handshake className="h-3 w-3 text-secondary" /> {t("hero", "eyebrow", tr("Como trabalhamos", "How we work"))}
                    </span>
                    <h1 className="mt-3 max-w-4xl display-xl">
                        {tr("Do briefing à ", "From briefing to ")}<span className="serif-accent text-secondary">{tr("entrega", "delivery")}</span>{tr(" — um processo desenhado para si.", " — a process designed around you.")}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "Em 6 a 8 semanas transformamos o seu briefing num carrinho NOVIFOOD pronto a operar.",
                            "In 6 to 8 weeks we turn your briefing into a NOVIFOOD cart ready to operate."
                        ))}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3">
                        <Link to={t("hero", "cta_primary_to", "/contacto")} data-testid="processos-cta-briefing" className="btn-premium-gold">
                            {t("hero", "cta_primary_label", tr("Iniciar briefing", "Start a briefing"))} <ArrowRight className="h-4 w-4 arrow-shift" />
                        </Link>
                        <Link to="/simulador" data-testid="processos-cta-simulador" className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-bold text-white backdrop-blur hover:bg-white/10 transition">
                            {tr("Experimentar o simulador", "Try the simulator")}
                        </Link>
                    </div>
                </div>
            </section>

            {/* JOURNEY MAP — visual 3-phase timeline */}
            <section className="border-b border-border bg-white" data-testid="processos-journey">
                <div className="container-x py-12 sm:py-16">
                    <div className="max-w-2xl mx-auto text-center">
                        <span className="eyebrow"><span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary mr-2 align-middle" />{tr("A jornada", "The journey")}</span>
                        <h2 className="mt-3 display-lg text-primary">
                            {tr("Três fases, um só ", "Three phases, one ")}<span className="serif-accent text-secondary">{tr("caminho", "path")}</span>.
                        </h2>
                        <p className="mt-3 text-muted-foreground">
                            {tr(
                                "Do primeiro email à primeira venda com o carrinho na rua — cada passo transparente e datado.",
                                "From the first email to the first street sale — every step transparent and scheduled."
                            )}
                        </p>
                    </div>

                    {/* Timeline row */}
                    <div className="relative mt-12">
                        {/* Connecting line (desktop only) */}
                        <div aria-hidden className="hidden md:block absolute left-[12%] right-[12%] top-8 h-[3px] bg-gradient-to-r from-secondary/30 via-secondary to-secondary/30" />
                        <div className="grid gap-6 md:grid-cols-3 relative">
                            {phases.map((p) => (
                                <a
                                    key={p.n}
                                    href={`#fase-${p.n}`}
                                    data-testid={`journey-card-${p.n}`}
                                    className="group relative flex flex-col rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:shadow-xl hover:border-secondary/50"
                                >
                                    {/* Number circle on top */}
                                    <div className="mx-auto -mt-14 mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary text-secondary shadow-lg ring-4 ring-white">
                                        <p.icon className="h-7 w-7" />
                                    </div>
                                    <div className="text-center">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-secondary">{tr("Fase", "Phase")} {p.n}</span>
                                        <h3 className="mt-1 font-heading text-2xl font-extrabold text-primary">{p.title}</h3>
                                        <p className="mt-1 text-sm text-muted-foreground">{p.kicker}</p>
                                    </div>
                                    {/* Duration chip */}
                                    <div className="mt-4 inline-flex mx-auto items-center gap-1.5 rounded-full bg-secondary/10 px-3 py-1 text-xs font-bold text-primary">
                                        <Clock className="h-3.5 w-3.5" />
                                        {p.duration}
                                    </div>
                                    {/* Deliverables */}
                                    <ul className="mt-5 space-y-2 text-sm">
                                        {p.deliverables.map((d) => (
                                            <li key={d} className="flex items-start gap-2 text-primary/80">
                                                <Check className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                                                <span>{d}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs">
                                        <span className="font-bold uppercase tracking-wider text-primary/60">{tr("Ver detalhe", "See detail")}</span>
                                        <ChevronRight className="h-4 w-4 text-secondary group-hover:translate-x-0.5 transition" />
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* STICKY progress bar — shows current phase as user scrolls */}
            <section className="border-b border-border bg-white sticky top-[68px] z-30" data-testid="processos-sticky-nav">
                <div className="container-x flex items-center gap-2 overflow-x-auto py-3">
                    {phases.map((p, i) => {
                        const isActive = active === p.n;
                        return (
                            <a
                                key={p.n}
                                href={`#fase-${p.n}`}
                                data-testid={`processos-anchor-${p.n}`}
                                className={
                                    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition " +
                                    (isActive
                                        ? "bg-primary text-secondary shadow-sm"
                                        : "text-primary/70 hover:bg-muted")
                                }
                            >
                                <span
                                    className={
                                        "inline-block h-2 w-2 rounded-full transition " +
                                        (isActive ? "bg-secondary" : "bg-primary/25")
                                    }
                                />
                                <span className={isActive ? "text-secondary" : "text-secondary/80"}>{p.n}</span>
                                {p.title}
                                {i < phases.length - 1 && (
                                    <span aria-hidden className={"ml-1 hidden sm:inline " + (isActive ? "text-secondary/60" : "text-primary/20")}>→</span>
                                )}
                            </a>
                        );
                    })}
                    <a href="#filosofia" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary/70 hover:bg-muted whitespace-nowrap ml-auto">
                        {tr("Filosofia", "Philosophy")}
                    </a>
                </div>
            </section>

            {/* PHASES */}
            {phases.map((p, idx) => (
                <div key={p.n || idx}>
                    <section
                        id={`fase-${p.n}`}
                        className={idx % 2 === 1 ? "bg-muted border-y border-border" : ""}
                        data-testid={`fase-${p.n}`}
                    >
                        <div className="container-x section-y">
                            <div className="grid lg:grid-cols-12 gap-6 sm:gap-10">
                                {/* LEFT — phase header */}
                                <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
                                    <div className="flex items-center gap-3">
                                        <span className="font-heading text-7xl lg:text-8xl font-extrabold text-secondary leading-none">{p.n}</span>
                                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-secondary">
                                            <p.icon className="h-6 w-6" />
                                        </div>
                                    </div>
                                    <span className="eyebrow mt-6 block">{p.kicker}</span>
                                    <h2 className="mt-3 display-lg text-primary">{p.title}</h2>

                                    {/* Duration + Stat card */}
                                    <div className="mt-5 flex flex-wrap gap-3">
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/15 border border-secondary/30 px-3 py-1.5 text-xs font-bold text-primary">
                                            <Clock className="h-3.5 w-3.5" /> {p.duration}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 border border-primary/10 px-3 py-1.5 text-xs font-bold text-primary/70">
                                            <Zap className="h-3.5 w-3.5 text-secondary" /> {p.steps.length} {tr("passos", "steps")}
                                        </span>
                                    </div>

                                    <p className="mt-5 text-muted-foreground leading-relaxed">{p.desc}</p>

                                    {p.stat && (
                                        <div className="mt-6 rounded-2xl border border-secondary/30 bg-white p-5 shadow-sm">
                                            <div className="flex items-baseline gap-3">
                                                <span className="font-heading text-4xl font-extrabold text-primary serif-accent">{p.stat.value}</span>
                                                <span className="text-sm text-muted-foreground">{p.stat.label}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Deliverable chips */}
                                    <div className="mt-6">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-secondary">{tr("O que recebes", "What you get")}</span>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {p.deliverables.map((d) => (
                                                <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border px-3 py-1 text-xs font-medium text-primary/80">
                                                    <Check className="h-3.5 w-3.5 text-secondary" /> {d}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT — steps with vertical connector */}
                                <div className="lg:col-span-7">
                                    <ol className="relative space-y-4 before:absolute before:left-[27px] before:top-6 before:bottom-6 before:w-[2px] before:bg-gradient-to-b before:from-secondary/50 before:via-secondary/30 before:to-transparent">
                                        {p.steps.map((st, i) => (
                                            <li
                                                key={`${p.n}-${i}`}
                                                data-testid={`fase-${p.n}-step-${i}`}
                                                className="relative rounded-2xl border border-border bg-white p-5 sm:p-6 transition hover:-translate-y-0.5 hover:shadow-lg hover:border-secondary/40"
                                            >
                                                <div className="flex gap-5">
                                                    {/* Numbered circle sits on the connector line */}
                                                    <div className="relative flex-shrink-0">
                                                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-secondary ring-4 ring-white shadow-sm">
                                                            <st.icon className="h-5 w-5" />
                                                        </div>
                                                        <span className="absolute -top-1 -right-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-primary text-[11px] font-extrabold shadow">
                                                            {i + 1}
                                                        </span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span className="text-xs uppercase tracking-[0.18em] font-bold text-secondary">{tr("Passo", "Step")} {String(i + 1).padStart(2, "0")}</span>
                                                            {p.durations[i] && (
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary/60 bg-muted rounded-full px-2 py-0.5">
                                                                    <Clock className="h-3 w-3" /> {p.durations[i]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <h3 className="mt-1 font-heading text-lg sm:text-xl font-bold text-primary">{st.label}</h3>
                                                        {st.text && <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">{st.text}</p>}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>

                                    {/* Inline CTA at end of each phase */}
                                    {p.cta && (
                                        <div className="mt-8" data-testid={`fase-${p.n}-inline-cta`}>
                                            <InlineCTA
                                                eyebrow={tr("Ver mais", "See more")}
                                                title={p.cta.title}
                                                to={p.cta.to}
                                                label={p.cta.label}
                                                icon={p.cta.icon}
                                                variant={idx % 2 === 0 ? "ghost" : "gold"}
                                                testId={`processos-inline-cta-${p.n}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            ))}

            {/* SEM vs COM NOVIFOOD */}
            <section className="border-y border-border bg-white" data-testid="processos-comparison">
                <div className="container-x section-y">
                    <div className="max-w-2xl mx-auto text-center">
                        <span className="eyebrow"><span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary mr-2 align-middle" />{tr("Porquê connosco", "Why us")}</span>
                        <h2 className="mt-3 display-lg text-primary">
                            {tr("Fazer sozinho ", "Doing it alone ")}<span className="serif-accent text-secondary">{tr("vs", "vs")}</span>{tr(" com a NOVIFOOD.", " with NOVIFOOD.")}
                        </h2>
                        <p className="mt-3 text-muted-foreground">
                            {tr(
                                "O mesmo objectivo, dois caminhos completamente diferentes.",
                                "Same goal, two very different paths."
                            )}
                        </p>
                    </div>

                    <div className="mt-12 grid gap-6 lg:grid-cols-2">
                        {/* Without */}
                        <div className="relative rounded-3xl border border-border bg-white p-6 sm:p-8 lg:p-10" data-testid="comparison-without">
                            <div className="flex items-center gap-3">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-primary/50">
                                    <X className="h-6 w-6" />
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-primary/50">{tr("Sem parceiro", "Without partner")}</span>
                                    <h3 className="font-heading text-2xl font-extrabold text-primary/70">{CONTRAST.outTitle}</h3>
                                </div>
                            </div>
                            <ul className="mt-6 space-y-3">
                                {CONTRAST.outItems.map((it, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-primary/60 line-through decoration-primary/20">
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/5 text-primary/40 flex-shrink-0 mt-0.5">
                                            <X className="h-3.5 w-3.5" />
                                        </span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* With */}
                        <div className="relative overflow-hidden rounded-3xl border-2 border-secondary/40 bg-primary p-6 sm:p-8 lg:p-10 text-white shadow-xl" data-testid="comparison-with">
                            <div aria-hidden className="absolute -top-20 -right-20 h-64 w-64 rounded-full opacity-25 pointer-events-none" style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }} />
                            <div className="relative flex items-center gap-3">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary shadow-lg">
                                    <Check className="h-6 w-6" />
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-secondary">{tr("Com parceiro", "With partner")}</span>
                                    <h3 className="font-heading text-2xl font-extrabold text-white">{CONTRAST.inTitle}</h3>
                                </div>
                            </div>
                            <ul className="relative mt-6 space-y-3">
                                {CONTRAST.inItems.map((it, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-white/90">
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-primary flex-shrink-0 mt-0.5 shadow">
                                            <Check className="h-3.5 w-3.5" />
                                        </span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="relative mt-8">
                                <Link
                                    to="/contacto"
                                    data-testid="comparison-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-3 text-sm font-bold text-primary shadow-lg hover:bg-yellow-300 transition hover:-translate-y-0.5"
                                >
                                    {tr("Quero este caminho", "I want this path")} <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY */}
            <section id="filosofia" className="bg-primary text-white" data-testid="processos-filosofia">
                <div className="container-x section-y">
                    <div className="max-w-3xl">
                        <span className="eyebrow text-secondary">{t("philosophy_intro", "eyebrow", tr("A filosofia NOVIFOOD", "The NOVIFOOD philosophy"))}</span>
                        <h2 className="mt-3 display-lg">
                            {t("philosophy_intro", "title", tr("Seis princípios que estão em cada parafuso.", "Six principles present in every bolt."))}
                        </h2>
                    </div>
                    <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {philosophy.map((it, i) => {
                            const Icon = resolveIcon(it.icon, "Sparkles");
                            return (
                                <div key={`${it.title}-${i}`} data-testid={`filosofia-${(it.title || "").toLowerCase().replace(/\s+/g, "-")}`}
                                    className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition hover:bg-white/10 hover:-translate-y-1">
                                    <Icon className="h-6 w-6 text-secondary" />
                                    <h3 className="mt-4 font-heading text-xl font-bold">{it.title}</h3>
                                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{it.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="container-x section-y-sm" data-testid="processos-final-cta">
                <CtaBanner
                    eyebrow={tr("Próximo passo", "Next step")}
                    title={tr("Pronto para começar o seu processo?", "Ready to start your process?")}
                    description={tr(
                        "Briefing gratuito em 30 minutos. Recebe uma estimativa de prazos e custo no mesmo dia — sem compromisso.",
                        "Free 30-minute briefing. Get a timeline and cost estimate the same day — no commitment."
                    )}
                    primary={{ to: "/contacto", label: tr("Marcar briefing", "Book a briefing") }}
                    secondary={{ to: `https://wa.me/${whatsappNumber}`, label: tr("Falar no WhatsApp", "Chat on WhatsApp"), external: true, icon: MessageCircle }}
                    variant="yellow"
                    testId="processos-final-cta-banner"
                />

                {/* Trust bar under final CTA */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-3">
                        <Zap className="h-5 w-5 text-secondary" />
                        <span className="text-xs sm:text-sm font-bold text-primary/80">{tr("Resposta em 24h", "24h reply SLA")}</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-3">
                        <MapPin className="h-5 w-5 text-secondary" />
                        <span className="text-xs sm:text-sm font-bold text-primary/80">{tr("Feito em Portugal", "Made in Portugal")}</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-3">
                        <ShieldCheck className="h-5 w-5 text-secondary" />
                        <span className="text-xs sm:text-sm font-bold text-primary/80">{tr("Garantia 2 anos", "2-year warranty")}</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-3">
                        <Palette className="h-5 w-5 text-secondary" />
                        <span className="text-xs sm:text-sm font-bold text-primary/80">{tr("100% personalizável", "100% customisable")}</span>
                    </div>
                </div>
            </section>
        </>
    );
}
